import { motion, useScroll, useTransform, useMotionValue, useInView, animate } from "framer-motion";
import { useRef, useEffect } from "react";

interface StepVisualProps {
    autoplay?: boolean;
}

const StyleGuidanceStep = ({ autoplay = false }: StepVisualProps = {}) => {
    const targetRef = useRef(null);
    const { scrollYProgress: scrollProg } = useScroll({
        target: targetRef,
        offset: ["start end", "end start"]
    });
    const autoMV = useMotionValue(0);
    const inView = useInView(targetRef, { amount: 0.4 });
    useEffect(() => {
        if (!autoplay) return;
        if (inView) {
            const controls = animate(autoMV, 1, { duration: 1.6, ease: "easeInOut", delay: 0.1 });
            return () => controls.stop();
        }
        autoMV.set(0);
    }, [autoplay, inView, autoMV]);
    const scrollYProgress = autoplay ? autoMV : scrollProg;

    return (
        <div ref={targetRef} className="relative w-full h-[400px] sm:h-[500px] bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-white" />
            <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] [background-size:24px_24px] opacity-40" />

            <div className="relative h-full w-full flex flex-col items-center justify-center perspective-1000">

                {/* Chat Interface */}
                <div className="w-full max-w-xs space-y-3 mb-6 px-4">
                    {/* User Message */}
                    <motion.div
                        className="flex justify-end"
                        style={{
                            opacity: useTransform(scrollYProgress, [0.1, 0.2], [0, 1]),
                            y: useTransform(scrollYProgress, [0.1, 0.2], [10, 0])
                        }}
                    >
                        <div className="bg-[#0f172a] text-white px-4 py-2.5 rounded-md rounded-tr-sm text-xs font-semibold tracking-wide shadow-xl max-w-[90%] border border-slate-700">
                            Need a look for the weekend! ✈️
                        </div>
                    </motion.div>

                    {/* Stylist Message */}
                    <motion.div
                        className="flex justify-start items-end gap-2"
                        style={{
                            opacity: useTransform(scrollYProgress, [0.2, 0.3], [0, 1]),
                            y: useTransform(scrollYProgress, [0.2, 0.3], [10, 0])
                        }}
                    >
                        <div className="w-6 h-6 rounded-sm bg-[#c2410c] flex items-center justify-center text-white text-[10px] font-bold shadow-md">
                            C
                        </div>
                        <div className="bg-white border border-slate-200 text-slate-800 px-4 py-2.5 rounded-md rounded-tl-sm text-xs font-semibold tracking-wide shadow-[0_10px_30px_rgba(0,0,0,0.08)] max-w-[90%]">
                            I've styled your <span className="font-bold text-[#c2410c]">fav gray pants</span> with a new jacket. 👇
                        </div>
                    </motion.div>
                </div>

                {/* Screenshots Container */}
                <div className="relative w-full max-w-xs px-4 h-[250px] z-20">
                    {/* Screenshot 1: Outfit Grid (Back Layer) */}
                    <motion.div
                        className="absolute top-0 left-0 w-[70%] rounded-md shadow-[0_20px_40px_rgba(0,0,0,0.15)] overflow-hidden border-[4px] border-white bg-white origin-bottom-left ring-1 ring-slate-200"
                        style={{
                            y: useTransform(scrollYProgress, [0.3, 0.4], [40, 0]),
                            opacity: useTransform(scrollYProgress, [0.3, 0.4], [0, 1]),
                            scale: useTransform(scrollYProgress, [0.4, 0.5], [1, 0.95]),
                            rotate: useTransform(scrollYProgress, [0.4, 0.5], [0, -5]),
                        }}
                    >
                        <img
                            src="/style-guidance-screenshot-2.png"
                            alt="Outfit Grid"
                            className="w-full h-auto"
                        />
                    </motion.div>

                    {/* Screenshot 2: Detail View (Front Layer) */}
                    <motion.div
                        className="absolute top-12 right-0 w-[70%] rounded-md shadow-[0_30px_60px_-15px_rgba(0,0,0,0.3)] overflow-hidden border-[4px] border-white bg-white ring-1 ring-slate-200"
                        style={{
                            y: useTransform(scrollYProgress, [0.4, 0.5], [40, 0]),
                            opacity: useTransform(scrollYProgress, [0.4, 0.5], [0, 1]),
                        }}
                    >
                        <img
                            src="/style-guidance-screenshot.png"
                            alt="Outfit Detail"
                            className="w-full h-auto"
                        />
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default StyleGuidanceStep;
